import React, { FC, useCallback } from "react";
import { Button, Tag, Tooltip, message } from "antd";
import { useTranslation } from "react-i18next";
import {
  useAddToSetMutation,
  useGetMembershipQuery,
} from "../../../../qualityLab/store/qualityLabApi";
import {
  QualityLabState,
  QualityLabStore,
} from "../../../../qualityLab/interfaces/qualityLab";
import { QUALITY_LAB_ENABLED } from "../../../../common/utilities/env";

type Props = {
  documentSetId: string;
};

const STATE_LABEL: Record<QualityLabState, string> = {
  [QualityLabState.Unverified]: "memory.stateUnverified",
  [QualityLabState.Verified]: "memory.stateVerified",
  [QualityLabState.Expert]: "memory.stateExpert",
};

/**
 * Contributes the open document to Memory, the set the extraction models learn
 * from. Offered on the detail screen because that is where someone recognises a
 * good example, rather than in the Quality Lab which they would have to think to
 * visit.
 *
 * Deliberately Ant Design rather than the KDS components the Quality Lab uses:
 * the KDS stylesheet only applies inside a `.kds` subtree, and this renders in
 * the detail footer, which is an Ant Design screen. Matching the host beats
 * matching the feature it links to, which is also why the button borrows the
 * footer's own translucent dark treatment instead of arriving as a white box.
 */
const AddToMemory: FC<Props> = ({ documentSetId }) => {
  const { t } = useTranslation("annotationSideControls");
  // Behind the same flag as the menu entry. This is the Quality Lab's other way
  // in, and it sits on a screen every user opens, so leaving it would put the
  // feature in front of more people than the menu ever did. `skip` keeps it from
  // calling the endpoint at all where the lab is switched off.
  const { data: membership, isLoading } = useGetMembershipQuery(documentSetId, {
    skip: !QUALITY_LAB_ENABLED,
  });
  const [addToSet, { isLoading: isAdding }] = useAddToSetMutation();

  const onAdd = useCallback(async () => {
    try {
      await addToSet({
        set: "memory",
        documentSetIds: [documentSetId],
      }).unwrap();
      message.success(t("memory.added"));
    } catch {
      message.error(t("memory.addFailed"));
    }
  }, [addToSet, documentSetId, t]);

  if (!QUALITY_LAB_ENABLED || isLoading || !membership) {
    return null;
  }

  if (membership.member) {
    // Already contributed, by this user or by the automatic process. Showing the
    // state rather than a disabled button explains why there is nothing to do.
    const inMemory = membership.store === QualityLabStore.Memory;
    return (
      <Tag color={inMemory ? "cyan" : "purple"}>
        {inMemory ? t("memory.inMemory") : t("memory.inEvaluation")}
        {membership.state ? ` · ${t(STATE_LABEL[membership.state])}` : ""}
      </Tag>
    );
  }

  // Not eligible means not approved or exported yet. No button rather than a
  // disabled one: the document simply is not finished, and it will become
  // eligible on its own.
  if (!membership.eligible) {
    return null;
  }

  return (
    <Tooltip title={t("memory.hint")}>
      <Button
        className="add-to-memory"
        loading={isAdding}
        onClick={onAdd}
        icon={<i className="bi bi-bookmark-plus" />}
      >
        {t("memory.add")}
      </Button>
    </Tooltip>
  );
};

export default AddToMemory;
